import { BaseService } from "@/api/base-service";
import type { Discipline, DisciplinesResponse } from "@/api/disciplines/types";
import { buildQueryParams } from "@/utils/build-query-params";

// The API serializes discipline ids as plain integers even though Discipline
// declares `id: string` — normalized here, once, so every consumer gets the
// type it was promised.
export function normalizeDiscipline(discipline: Discipline): Discipline {
  return { ...discipline, id: String(discipline.id) };
}

function normalizeDisciplinesResponse(
  response: DisciplinesResponse,
): DisciplinesResponse {
  return { disciplines: response.disciplines.map(normalizeDiscipline) };
}

export class DisciplinesService extends BaseService {
  static get instance() {
    return this.getInstance<DisciplinesService>();
  }

  public async getDisciplines(): Promise<DisciplinesResponse> {
    const response = await this.client.get<DisciplinesResponse>("/disciplines");
    return normalizeDisciplinesResponse(response);
  }

  public async getDiscipline(id: string): Promise<Discipline> {
    const response = await this.client.get<{ discipline: Discipline }>(
      `/disciplines/${id}`,
    );
    return normalizeDiscipline(response.discipline);
  }

  public async getDisciplineByGymId(
    gymId: number,
  ): Promise<DisciplinesResponse> {
    return this.getDisciplinesByGyms([gymId]);
  }

  public async getDisciplinesByGyms(
    gymIds: number[],
  ): Promise<DisciplinesResponse> {
    if (gymIds.length === 0) {
      return { disciplines: [] };
    }

    const response = await this.client.get<DisciplinesResponse>(
      `/disciplines?${buildQueryParams({ gymIds })}`,
    );
    return normalizeDisciplinesResponse(response);
  }

  public async createDiscipline(data: FormData): Promise<Discipline> {
    const response = await this.client.post<{ discipline: Discipline }>(
      `/disciplines`,
      data,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return normalizeDiscipline(response.discipline);
  }

  public async updateDiscipline(
    id: string,
    data: FormData,
  ): Promise<Discipline> {
    const response = await this.client.patch<{ discipline: Discipline }>(
      `/disciplines/${id}`,
      data,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return normalizeDiscipline(response.discipline);
  }

  public async deleteDisciplines(ids: number[]): Promise<void> {
    await this.client.delete(`/disciplines`, {
      data: { ids },
    });
  }
}
